import React, { useState, useEffect, useMemo } from 'react';
import HallwaySchedule from './components/HallwaySchedule';
import SchedulePopup from './components/SchedulePopup';
import { Player, ChatMessage, RoomData, Wing, RoomAccessRule, WingAccessRule } from './types';
import { AVATARS } from './constants';
import { isStaff, isAdmin } from './utils/roles';
import Room from './components/Room';
import Chat from './components/Chat';
import DragonStatue from './components/DragonStatue';
import GuardianMirror from './components/GuardianMirror';
import TheForge from './components/TheForge';
import Hallway3D from './components/Hallway3D';
import { checkAccessFor } from './utils/access';
import PlayerProfile from './components/PlayerProfile';
import { presencePublic } from './utils/presencePublic'; // PRESENCE-PRIVACY-V1

// Import fra din nye centrale config i rodmappen
import { 
  db, 
  auth, 
  onAuthStateChanged, 
  signInWithEmailAndPassword, 
  doc, 
  getDoc, 
  setDoc, 
  deleteDoc, 
  safeOnSnapshot, 
  collection, 
  addDoc, 
  query, 
  orderBy, 
  limit, 
  where, 
  getDocs, 
  writeBatch, 
  onSnapshot,
  updateDoc 
} from './firebaseConfig';
import { signInWithCustomToken } from 'firebase/auth';
import { openInHalls } from './utils/sso';
import { functions, httpsCallable } from './firebaseConfig';
import { logSisterAction } from './sisterAudit';
import { gateKind, gateHref, TRIAL_TXT, trialLang } from './utils/proeveSlut'; // PROEVE-UDLOEB-V1

// CAMPUS-PAY-GATE-V1 (21-9-2026): familiens betalingsstatus (membershipStatus) til abonnement-porten. Den saettes i
// brugerens snapshot lige foer setMe og ligger bevidst IKKE i Player, fordi Player skrives til presence.
let _memberStatus = '';
// CAMPUS-SIDST-SET-V1 (Tanja 2-11-2026): en elev, hvis adgang er lukket (porten nedenfor), skrev stadig presence hvert 20. sekund, saa den
// gemte session viste hende som logget ind (Aia Just, lukket 22-10). Nu skrives presence ikke for en lukket konto; 'Sidst set' laeses af
// presence.lastSeen (aeldre end 10 minutter vises ikke som online).
let _campusLukket = false;
// PROEVE-UDLOEB-V1: proeve-udloeb (HQ runTrialExpiry) til porten.
let _subStatus = '';
let _trialExpiredAt: unknown = null;
let _trialProdukt = '';
let _hasOnline = false;
// Porte-teksterne som noegle + dansk kilde.
const GATE_TXT: Record<string, string> = {
  title: 'Adgang forseglet',
  banned: 'Din adgang til Campus er spærret. Kontakt Akademiet, hvis du mener, det er en fejl.',
  pay_student: 'Adgangen til Campus er sat på pause, fordi betalingen mangler. Sig det til dine forældre, så de kan ordne det på norewyn.com. Så åbner Campus igen af sig selv.',
  pay_parent: 'Jeres adgang er sat på pause, fordi betalingen mangler. Gå til din profil på norewyn.com og opdatér betalingen. Så åbner Campus igen af sig selv.',
  closed_student: 'Din adgang til Campus er lukket. Sig det til dine forældre, hvis du vil fortsætte. De kan vælge et medlemskab på norewyn.com.',
  closed_parent: 'Jeres adgang til Campus er lukket. Vælg et medlemskab i Handelsposten på norewyn.com, så åbner Campus igen, så snart betalingen er registreret.',
  pay_cta: 'Opdatér betalingen',
  closed_cta: 'Vælg medlemskab',
  logout: 'Log ud',
};

const s = (val: any): string => {
  if (typeof val === 'string') return val;
  if (!val) return '';
  if (typeof val === 'object') {
    return val.da || val.en || val.nb || Object.values(val).find(v => typeof v === 'string') || "";
  }
  return String(val);
};

const App: React.FC = () => {
  const [rooms, setRooms] = useState<Record<string, RoomData>>({});
  const [wings, setWings] = useState<Wing[]>([]);
  const [roomRules, setRoomRules] = useState<Record<string, RoomAccessRule>>({});
  const [wingRules, setWingRules] = useState<Record<string, WingAccessRule>>({});
  const [currentWingId, setCurrentWingId] = useState<string>('hub');
  const [me, setMe] = useState<Player | null>(null);
  const [others, setOthers] = useState<Player[]>([]);
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [showGuardianMirror, setShowGuardianMirror] = useState(false);
  const [showTheForge, setShowTheForge] = useState(false);
  const [forgeInitialRoomId, setForgeInitialRoomId] = useState<string | undefined>();
  const [selectedPlayer, setSelectedPlayer] = useState<Player | null>(null);
  const [guideMessage, setGuideMessage] = useState<string>("Velkommen til Norewyn.");
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isAdminPreview, setIsAdminPreview] = useState(false);
  const [isPortalActive, setIsPortalActive] = useState(false);
  const [academyUrl, setAcademyUrl] = useState<string>("");
  const [pendingRoomHash, setPendingRoomHash] = useState<string | null>(null);
  const [emailInput, setEmailInput] = useState("");
  const [passwordInput, setPasswordInput] = useState("");
  const [loginError, setLoginError] = useState<string | null>(null);
  const [authChecking, setAuthChecking] = useState(true);

  // Beregn den aktuelle chat-kanal baseret på hvor man står
  const currentChatChannel = useMemo(() => {
    if (!me) return 'global';
    if (me.currentRoom === 'HALLWAY') {
      return `wing_${currentWingId}`;
    }
    return `room_${me.currentRoom}`;
  }, [me?.currentRoom, currentWingId]);

  // Hent det menneskelige navn til chat-titlen
  const currentLocationName = useMemo(() => {
    if (!me) return 'Akademiet';
    if (me.currentRoom === 'HALLWAY') {
      if (currentWingId === 'hub') return 'Hovedhallen';
      const wing = wings.find(w => w.id === currentWingId);
      return s(wing?.name) || 'Sektor';
    }
    const room = rooms[me.currentRoom];
    return s(room?.name) || 'Rum';
  }, [me?.currentRoom, currentWingId, wings, rooms]);

  // Synkronisering af rum-regler
  useEffect(() => {
    const unsub = safeOnSnapshot(collection(db, "room_rules"), (snap: any) => {
      const rules: Record<string, RoomAccessRule> = {};
      snap.forEach((d: any) => { rules[d.id] = d.data() as RoomAccessRule; });
      setRoomRules(rules);
    });
    return () => unsub();
  }, []);
  // Synkronisering af wing-regler
  useEffect(() => {
    const unsub = safeOnSnapshot(collection(db, "wing_rules"), (snap: any) => {
      const wRules: Record<string, WingAccessRule> = {};
      snap.forEach((d: any) => { wRules[d.id] = { id: d.id, ...d.data() } as WingAccessRule; });
      setWingRules(wRules);
    });
    return () => unsub();
  }, []);

  // Automatisk oprydning af beskeder ældre end 24 timer
  useEffect(() => {
    const cleanupOldMessages = async () => {
      if (!me || isStaff(me.role) === false) return;
      
      const twentyFourHoursAgo = Date.now() - (24 * 60 * 60 * 1000);
      const q = query(collection(db, "chats"), where("timestamp", "<", twentyFourHoursAgo));
      const snap = await getDocs(q);
      
      if (!snap.empty) {
        console.log(`🧹 Rydder op: Sletter ${snap.size} gamle beskeder fra arkivet.`);
        const batch = writeBatch(db);
        snap.docs.forEach(d => batch.delete(d.ref));
        await batch.commit();
      }
    };
    cleanupOldMessages();
  }, [me?.id, me?.role]);

  // Synkronisering af Chat Beskeder
  useEffect(() => {
    if (!me) return;
    const chatRef = collection(db, "chats");
    const q = query(
      chatRef, 
      where("room", "==", currentChatChannel),
      orderBy("timestamp", "desc"), 
      limit(50)
    );
    
    const unsub = safeOnSnapshot(q, (snap: any) => {
      const msgs: ChatMessage[] = [];
      snap.forEach((d: any) => msgs.push({ id: d.id, ...d.data() } as ChatMessage));
      setMessages(msgs.reverse());
    });
    return () => unsub();
  }, [me?.id, currentChatChannel]);

  // Presence & Others sync
  useEffect(() => {
    if (!me) return;
    const q = query(collection(db, "presence"));
    const unsub = safeOnSnapshot(q, (snap: any) => {
      const players: Player[] = [];
      snap.forEach((d: any) => {
        if (d.id !== me.id && (Date.now() - d.data().lastSeen < 600000)) {
          players.push({ id: d.id, ...d.data() } as Player);
        }
      });
      setOthers(players);
    });
    return () => unsub();
  }, [me]);

  // Global Config Sync
  useEffect(() => {
    const unsub = safeOnSnapshot(doc(db, "rooms", "config_global"), (snap: any) => {
      if (snap.exists()) {
        setAcademyUrl(snap.data().mainAcademyUrl || "");
      }
    });
    return () => unsub();
  }, []);

  // Wings Sync
  useEffect(() => {
    const q = query(collection(db, "wings"));
    const unsubWings = safeOnSnapshot(q, (snapshot: any) => {
      const updated: Wing[] = [];
      snapshot.forEach((doc: any) => { updated.push({ ...doc.data(), id: doc.id } as Wing); });
      setWings(updated.sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0)));
    });
    return () => unsubWings();
  }, []);

  // Rooms Sync
  useEffect(() => {
    const unsubRooms = safeOnSnapshot(collection(db, "rooms"), (snapshot: any) => {
      const updated: Record<string, RoomData> = {};
      snapshot.forEach((doc: any) => { 
        if (doc.id !== 'config_global') {
          updated[doc.id] = { ...doc.data(), id: doc.id }; 
        }
      });
      setRooms(updated);
    });
    return () => unsubRooms();
  }, []);

  // OPDATERET AUTH LYTTER (Fletning af campus og søsterside)
  useEffect(() => {
    // SSO fra søstersider (Halls/HQ): modtag custom-token og log ind
    try {
      const _params = new URLSearchParams(window.location.search);
      const _ssoToken = _params.get('sso');
      if (_ssoToken) {
        _params.delete('sso');
        const _clean = window.location.pathname + (_params.toString() ? '?' + _params.toString() : '') + window.location.hash;
        window.history.replaceState({}, '', _clean);
        signInWithCustomToken(auth, _ssoToken).catch((e) => console.warn('SSO login fejlede:', e));
      }
    } catch (e) { console.warn('SSO parse fejl:', e); }
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthChecking(true);
      if (user && !isAdminPreview) {
        // Direkte uid-lookup — hurtigere, ingen index krav
        try { var _uid = user.uid; if (_uid && typeof sessionStorage!=='undefined' && !sessionStorage.getItem('ssb_campus')) { sessionStorage.setItem('ssb_campus','1'); import('firebase/firestore').then(async function(m){ var _db=m.getFirestore(); var _s=await m.getDoc(m.doc(_db,'users',_uid)); var _d=_s.exists()?_s.data():{}; var _role=String((_d.role)||'').toLowerCase(); if(['staff','admin','guardian','superadmin'].indexOf(_role)>=0){ m.addDoc(m.collection(_db,'login_events'),{ userId:_uid, userName:(_d.username||_d.name||''), userRole:_role, ts:Date.now(), site:'campus' }); } }).catch(function(){}); } } catch(e){}
        let _presenceInit: boolean = false;
        const unsub = onSnapshot(doc(db, "users", user.uid), async (snap) => {
          const d = snap.exists() ? snap.data() : null;
          const _seed = Math.abs(Array.from(user.uid).reduce((a, c) => ((a << 5) - a + c.charCodeAt(0)) | 0, 0));
          _memberStatus = String((d && d.membershipStatus) || ''); // CAMPUS-PAY-GATE-V1: til porten, ikke i Player (Player skrives til presence)
          _campusLukket = !isStaff(String((d && d.role) || 'student')) && (d?.isBanned === true || d?.hasHallsAccess !== true || ['active', 'lifetime'].includes(_memberStatus) === false); // CAMPUS-SIDST-SET-V1 samme regel som porten
          _subStatus = String((d && d.subscriptionStatus) || ''); _trialExpiredAt = (d && d.trialExpiredAt) || null; _trialProdukt = String((d && d.trialExpiredProdukt) || ''); _hasOnline = !!(d && d.hasOnlineClassroomAccess === true); // PROEVE-UDLOEB-V1
          const playerInfo: Player = {
            id: user.uid,
            name: s(d?.name || user.displayName || user.email?.split("@")[0] || "Eventyrer"),
            wizardName: s(d?.username || d?.wizardName || ""),
            realName: s(d?.realName || ""),
            avatarUrl: d?.customAvatar || d?.avatarUrl || AVATARS[0],
            role: d?.role || "student",
            magicalLevel: d?.level ?? d?.magicalLevel ?? 1,
            xp: d?.xp ?? 0,
            allowedRooms: d?.allowedRooms || [],
            guild: d?.guild || d?.house || "",
            ageGroup: d?.ageGroup || "",
            classroomId: d?.classroomId || "",
            groupId: d?.groupId || "",
            campusPackage: d?.campusPackage || d?.packageType || "basis",
            hasHallsAccess: d?.hasHallsAccess === true,
            isBanned: d?.isBanned === true,
            isIdentityVerified: d?.isIdentityVerified === true,
            subjectIds: d?.subjectIds || [],
            classIds: d?.classIds || d?.hallIds || [],
            x: 12 + (_seed % 76), y: 66 + (Math.floor(_seed / 89) % 24), currentRoom: "HALLWAY", lastSeen: Date.now()
          };
          setMe(prev => prev ? { 
            ...playerInfo, 
            currentRoom: prev.currentRoom,
            x: prev.x,
            y: prev.y 
        } : playerInfo);
// Hash-navigation — gå direkte til rum ved direkte link
setTimeout(() => {
    const hash = window.location.hash.replace('#', '');
    if (hash && !wings.some(w => w.id === hash) && hash !== 'hub') {
        handleDoorClick(hash);
    }
}, 1000);
          // PRESENCE-PRIVACY-V1 (21-9-2026): presence kan laeses af alle indloggede. Kun det, andre spillere bruger, skrives
          // (utils/presencePublic.ts), og de private felter fra foer slettes ved hvert login.
          if (_campusLukket) {
            // CAMPUS-SIDST-SET-V1: lukket konto skriver intet i presence
          } else if (_presenceInit === true) {
            const _identity: any = presencePublic(playerInfo);
            delete _identity.currentRoom; delete _identity.x; delete _identity.y;
            await setDoc(doc(db, "presence", user.uid), _identity, { merge: true });
          } else {
            _presenceInit = true;
            await setDoc(doc(db, "presence", user.uid), presencePublic(playerInfo), { merge: true });
          }
          setAuthChecking(false);
        });
        return () => unsub();
      } else {
        setMe(null);
        setAuthChecking(false);
      }
    });
    return () => unsubscribe();
  }, [isAdminPreview]);

  // Auto timeout i room
  useEffect(() => {
    if (!me) return;
    const interval = setInterval(() => {
        if (_campusLukket) return; // CAMPUS-SIDST-SET-V1
        setDoc(doc(db, "presence", me.id), { lastSeen: Date.now() }, { merge: true });
    }, 20000);
    return () => clearInterval(interval);
}, [me?.id]);

  // Adgangs-tjek - wrapper omkring den centrale checkAccessFor i utils/access.ts
  const [viewAs, setViewAs] = useState<any | null>(null);
  const [viewAsList, setViewAsList] = useState<any[]>([]);
  useEffect(() => {
    if (!me || isStaff(me.role) === false) return;
    getDocs(query(collection(db, 'users'), where('role', '==', 'student'))).then(snap => {
      const list: any[] = [];
      snap.forEach(d => list.push({ id: d.id, ...(d.data() as any) }));
      list.sort((a, b) => String(a.username || a.name || '').localeCompare(String(b.username || b.name || '')));
      setViewAsList(list);
      try { const sv = sessionStorage.getItem('norewyn.campusViewAs'); if (sv) { const hit = list.find(u => u.id === sv); if (hit) setViewAs(hit); } } catch (e) {}
    }).catch(() => {});
  }, [me?.id, me?.role]);
  const viewer: any = viewAs ? { ...me, ...viewAs, id: viewAs.id, role: 'student' } : me;
  const checkAccess = (targetId: string) =>
    checkAccessFor(viewer, targetId, { rooms, wings, roomRules, wingRules });

  const handleSendMessage = async (text: string) => {
    if (!me) return;
    await addDoc(collection(db, "chats"), {
      senderId: me.id,
      senderName: me.wizardName || me.name,
      text,
      timestamp: Date.now(),
      room: currentChatChannel
    });
  };

  const handleClearChat = async () => {
    if (!me || isStaff(me.role) === false) return;
    const q = query(collection(db, "chats"), where("room", "==", currentChatChannel));
    const snap = await getDocs(q);
    if (!snap.empty) {
      const batch = writeBatch(db);
      snap.docs.forEach(d => batch.delete(d.ref));
      try {
        await batch.commit();
      } catch (err: any) {
        console.error('clearChat commit fejlede:', err);
        setGuideMessage('Arkivet kunne ikke renses: ' + ((err && (err.code || err.message)) || 'ukendt fejl'));
        return;
      }
      await addDoc(collection(db, "chats"), {
        senderId: 'system',
        senderName: 'System',
        text: 'En Vogter har renset arkivet for dette rum.',
        timestamp: Date.now(),
        room: currentChatChannel
      });
    }
  };

  const handleFloorClick = (e: React.MouseEvent) => {
    if (isTransitioning || !me || isPortalActive) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const newPos = { ...me, x, y, lastSeen: Date.now() };
    setMe(newPos);
    setDoc(doc(db, "presence", me.id), { x, y, currentRoom: newPos.currentRoom, lastSeen: newPos.lastSeen }, { merge: true }); // PRESENCE-PRIVACY-V1: kun position og rum, aldrig hele spilleren
  };

  const [returnRoomId, setReturnRoomId] = useState<string>('');
  const [showHallSchedule, setShowHallSchedule] = useState<boolean>(false);
  const handleScheduleNavigate = (targetId: string) => {
    if (me && me.currentRoom && me.currentRoom !== 'HALLWAY') setReturnRoomId(me.currentRoom);
    handleDoorClick(targetId);
  };
  const handleDoorClick = (targetId: string) => {
    if (isTransitioning || !me) return;
    const access = checkAccess(targetId);
    if (!access.hasAccess) {
        setGuideMessage(access.reason || "Dine kræfter rækker ikke til at åbne denne dør endnu.");
        return;
    }
    setIsTransitioning(true);
    setTimeout(async () => {
        const isWing = wings.some(w => w.id === targetId) || targetId === 'hub';
        const updates: Partial<Player> = { x: 15 + Math.random() * 70, y: 70 + Math.random() * 18, lastSeen: Date.now() };
        if (isWing) {
            setCurrentWingId(targetId);
            updates.currentRoom = 'HALLWAY';
            window.location.hash = '';
        } else {
            updates.currentRoom = targetId;
            window.location.hash = targetId;
        }
        setMe(prev => prev ? ({ ...prev, ...updates }) : null);
        await setDoc(doc(db, "presence", me.id), updates, { merge: true });
        setIsTransitioning(false);
    }, 600);
};

  // Deep-link: gaa direkte ind i rummet — saet baade wing og room (ingen mellem-doere)
  useEffect(() => {
    if (!me || Object.keys(rooms).length === 0) return;
    const hash = window.location.hash.replace('#', '');
    if (!hash || hash === 'hub' || wings.some(w => w.id === hash)) return;
    const room: any = (rooms as any)[hash];
    if (!room || me.currentRoom === hash) return;
    const access = checkAccess(hash);
    if (!access.hasAccess) { setGuideMessage(access.reason || 'Ingen adgang til dette rum.'); return; }
    const wingId = room.wingId || (Array.isArray(room.wingIds) && room.wingIds[0]) || currentWingId;
    if (wingId) setCurrentWingId(wingId);
    const updates: Partial<Player> = { currentRoom: hash, x: 50, y: 85, lastSeen: Date.now() };
    setMe(prev => prev ? ({ ...prev, ...updates }) : prev);
    setDoc(doc(db, "presence", me.id), updates, { merge: true });
  }, [me?.id, me?.currentRoom, rooms, wings, currentWingId]);
  const handleOpenForge = (roomId?: string) => {
    setShowGuardianMirror(false);
    setForgeInitialRoomId(roomId);
    setShowTheForge(true);
  };

  const handleOpenMirror = () => {
    setShowTheForge(false);
    setShowGuardianMirror(true);
  };

  if (authChecking) return (
    <div className="fixed inset-0 bg-[#05050a] flex flex-col items-center justify-center z-[1000]">
      <div className="font-cinzel text-yellow-500 text-3xl tracking-[0.4em] animate-pulse mb-4 uppercase">Norewyn</div>
      <div className="w-48 h-1 bg-white/5 rounded-full overflow-hidden">
        <div className="w-1/2 h-full bg-yellow-500 animate-[loading_2s_infinite]" />
      </div>
    </div>
  );

  if (!me) return (
    <div className="fixed inset-0 bg-[#05050a] flex items-center justify-center p-6 z-[500] overflow-hidden">
      <div className="absolute inset-0 bg-[url('https://images.unsplash.com/photo-1514373941175-0a1410229b4c?q=80&w=2000')] bg-cover bg-center opacity-20 scale-110" />
      <div className="absolute inset-0 bg-gradient-to-b from-indigo-950/40 via-[#05050a] to-[#05050a]" />
      <div className="relative w-full max-w-md bg-[#0a0a15]/90 border border-yellow-500/20 p-10 rounded-[2.5rem] shadow-2xl backdrop-blur-xl animate-fade-in">
        <div className="text-center mb-10">
          <h1 className="text-4xl font-cinzel text-white mb-2 uppercase tracking-tight">Norewyn</h1>
          <p className="text-yellow-500 font-cinzel text-[10px] tracking-[0.4em] uppercase opacity-70">Academy Campus Portal</p>
        </div>
        <form onSubmit={async (e) => {
          e.preventDefault();
          try {
            let loginEmail = emailInput;
            if (emailInput.includes("@")) {
              loginEmail = emailInput.trim().toLowerCase();
              await signInWithEmailAndPassword(auth, loginEmail, passwordInput);
            } else {
              try {
                const res = await httpsCallable(functions, 'usernameLogin')({ username: emailInput.trim(), password: passwordInput });
                await signInWithEmailAndPassword(auth, (res.data as any).email, passwordInput);
              } catch (cfErr) {
                const derived = emailInput.trim().toLowerCase().replace(/[^a-z0-9]/g, "") + "@norewyn.academy";
                await signInWithEmailAndPassword(auth, derived, passwordInput);
              }
            }
          } catch (error) {
            setLoginError("De magiske ord fejlede. Prøv igen.");
          }
        }} className="space-y-6">
          <div className="space-y-2">
            <label className="text-[10px] font-black text-yellow-500/80 uppercase tracking-widest ml-1">Email eller Brugernavn</label>
            <input type="text" value={emailInput} onChange={e => setEmailInput(e.target.value)} className="w-full bg-white/5 border border-white/10 p-4 rounded-xl text-white outline-none focus:border-yellow-500/50 transition-all placeholder:text-white/30" placeholder="din@magi.dk eller brugernavn" required />
          </div>
          <div className="space-y-2">
            <label className="text-[10px] font-black text-yellow-500/80 uppercase tracking-widest ml-1">Hemmeligt Kodeord</label>
            <input type="password" value={passwordInput} onChange={e => setPasswordInput(e.target.value)} className="w-full bg-white/5 border border-white/10 p-4 rounded-xl text-white outline-none focus:border-yellow-500/50 transition-all" required />
          </div>
          {loginError && <p className="text-red-500 text-[10px] text-center font-bold uppercase tracking-widest animate-pulse">{loginError}</p>}
          <button className="w-full bg-yellow-600 hover:bg-yellow-500 text-white py-5 rounded-2xl font-black uppercase tracking-[0.2em] transition-all shadow-xl hover:shadow-yellow-500/20 active:scale-95 border-b-4 border-yellow-800 mt-4">Træd Ind</button>
        </form>
        <p className="mt-8 text-center text-[9px] text-white/40 uppercase tracking-widest italic">"Videnskab er bare magi vi endnu ikke har forstået"</p>
      </div>
    </div>
  );

  // Abonnement-gate: kun berettigede (hasHallsAccess) + ikke-bannede. Staff altid.
  // Live via onSnapshot -> ud straks ved lapse, ind igen straks ved fornyelse (ingen gen-login).
  // CAMPUS-PAY-GATE-V1 (21-9-2026): ogsaa familiens betalingsstatus skal vaere active eller lifetime, samme regel som Halls.
  // Forfalden eller Udloebet (manuelt i Raadets kammer eller fra Stripe) lukker nu ogsaa Campus. Eleven faar ingen knap.
  // PROEVE-UDLOEB-V1: arten vaelges i utils/proeveSlut.ts (testet). Ny art 'trial' foer 'closed'.
  const _gk = gateKind({ role: me.role, isBanned: me.isBanned, hasHallsAccess: me.hasHallsAccess, hasOnlineClassroomAccess: _hasOnline, memberStatus: _memberStatus, subscriptionStatus: _subStatus, trialExpiredAt: _trialExpiredAt });
  if (_gk) {
    const _isKid = me.role === 'student';
    const _gateKind = _gk;
    const _TT = TRIAL_TXT[trialLang(typeof navigator !== 'undefined' ? navigator.language : '')];
    const _gateMsg = _gateKind === 'banned' ? GATE_TXT.banned : _gateKind === 'trial' ? (_isKid ? _TT.student : _TT.parent) : GATE_TXT[_gateKind + (_isKid ? '_student' : '_parent')];
    const _gateHref = gateHref(_gateKind, _trialProdukt);
    return (
    <div className="fixed inset-0 bg-[#05050a] flex items-center justify-center p-6 z-[500]">
      <div className="relative w-full max-w-md bg-[#0a0a15]/90 border border-yellow-500/20 p-10 rounded-[2.5rem] shadow-2xl backdrop-blur-xl text-center">
        <h1 className="text-3xl font-cinzel text-white mb-3 uppercase tracking-tight">{_gateKind === 'trial' ? _TT.title : GATE_TXT.title}</h1>
        <p className="text-white/70 text-sm mb-6 leading-relaxed">{_gateMsg}</p>
        {_gateKind !== 'banned' && _isKid === false && (
          <a href={_gateHref} className="inline-block bg-yellow-600 hover:bg-yellow-500 text-white py-4 px-8 rounded-2xl font-black uppercase tracking-[0.2em] transition-all border-b-4 border-yellow-800">{_gateKind === 'pay' ? GATE_TXT.pay_cta : _gateKind === 'trial' ? _TT.cta : GATE_TXT.closed_cta}</a>
        )}
        <button onClick={() => auth.signOut()} className="block mx-auto mt-6 text-[10px] text-white/40 uppercase tracking-widest hover:text-white/70 transition">{GATE_TXT.logout}</button>
      </div>
    </div>
    );
  }
    // Identitets-gate (forældre-verifikation): ikke-staff uden bekræftet identitet blokeres.
  if (!isStaff(me.role) && !me.isIdentityVerified) return (
    <div className="fixed inset-0 bg-[#05050a] flex items-center justify-center p-6 z-[500]">
      <div className="relative w-full max-w-md bg-[#0a0a15]/90 border border-yellow-500/20 p-10 rounded-[2.5rem] shadow-2xl backdrop-blur-xl text-center">
        <h1 className="text-3xl font-cinzel text-white mb-3 uppercase tracking-tight">Seglkammeret</h1>
        <p className="text-white/70 text-sm mb-2 leading-relaxed font-semibold">Dit segl afventer bekræftelse</p>
        <p className="text-white/60 text-sm mb-6 leading-relaxed">For at beskytte alle i Norewyn Akademiet skal en forælder bekræfte sin identitet, før adgangen åbnes. Forælderen logger ind på norewyn.com og gennemfører verificeringen på sin egen profil — det tager kun et øjeblik og kræver et gyldigt ID (pas eller kørekort). Når seglet er bekræftet, åbner adgangen automatisk.</p>
        <p className="text-white/40 text-xs mb-6 italic">Har du allerede verificeret? Kontakt en af Akademiets Vogtere.</p>
        <a href="https://norewyn.com" className="inline-block text-yellow-400/90 hover:text-yellow-300 text-sm font-semibold underline underline-offset-4 mb-5">Gå til norewyn.com →</a>
        <button onClick={() => auth.signOut()} className="block mx-auto text-[10px] text-white/40 uppercase tracking-widest hover:text-white/70 transition">Forlad Portalen</button>
      </div>
    </div>
  );
return (
    <div className="w-screen h-screen overflow-hidden bg-[#05050a] text-white relative font-quicksand">
      {/* MAGICAL XP BAR - TOP */}
      <div className="fixed top-0 inset-x-0 z-[500] h-1 bg-indigo-950 flex">
         <div className="h-full bg-yellow-500 shadow-[0_0_10px_rgba(234,179,8,1)] transition-all duration-1000" style={{ width: `${(((me.xp ?? 0) % 1000) / 1000) * 100}%` }} />
      </div>

      {isStaff(me.role) && viewAsList.length > 0 && (
        <div className="fixed top-2 left-1/2 -translate-x-1/2 z-[560] flex items-center gap-2 bg-purple-950/90 border border-purple-500/40 rounded-2xl px-3 py-1.5 shadow-2xl backdrop-blur-md">
          <span className="text-[9px] font-black uppercase tracking-widest text-purple-300">Se som</span>
          <select value={viewAs ? viewAs.id : ''} onChange={(e) => { const u = viewAsList.find(x => x.id === e.target.value) || null; setViewAs(u); try { if (u) sessionStorage.setItem('norewyn.campusViewAs', u.id); else sessionStorage.removeItem('norewyn.campusViewAs'); } catch (er) {} }} className="bg-black border border-purple-500/30 rounded-lg px-2 py-1 text-white text-xs max-w-[170px]">
            <option value="">Min egen visning</option>
            {viewAsList.map(u => <option key={u.id} value={u.id}>{u.username || u.name || u.id}</option>)}
          </select>
          {viewAs && <button onClick={() => { setViewAs(null); try { sessionStorage.removeItem('norewyn.campusViewAs'); } catch (er) {} }} className="text-purple-200 text-xs font-black">{'\u2715'}</button>}
        </div>
      )}
      {viewAs && <div className="fixed inset-0 z-[555] pointer-events-none border-4 border-purple-500/60" />}
      {isTransitioning && (
        <div className="fixed inset-0 z-[600] bg-[#05050a] flex items-center justify-center animate-fade-in">
          <div className="w-16 h-16 border-4 border-yellow-500/10 border-t-yellow-500 rounded-full animate-spin" />
        </div>
      )}

      {me.currentRoom === 'HALLWAY' ? (
        <Hallway3D rooms={Object.values(rooms)} wings={wings} currentWingId={currentWingId} me={me} others={others} onFloorClick={handleFloorClick} onDoorClick={handleDoorClick} onBackToHub={() => handleDoorClick('hub')} checkAccess={checkAccess} onPlayerClick={setSelectedPlayer} viewer={viewer} scheduleCard={<HallwaySchedule currentUser={viewer} rooms={rooms} onNavigateToRoom={handleScheduleNavigate} onOpenFullSchedule={() => setShowHallSchedule(true)} />} />
      ) : (
        rooms[me.currentRoom] && (
          <Room 
            room={rooms[me.currentRoom]} 
            me={me} 
            occupants={others.filter(o => o.currentRoom === me.currentRoom).concat(me)} 
            guideMessage={guideMessage} 
            onExit={() => handleDoorClick(currentWingId)} 
            onFloorClick={handleFloorClick} 
            onPortalToggle={setIsPortalActive} 
            onOpenForge={handleOpenForge}
              onNavigateToRoom={handleScheduleNavigate}
            onPlayerClick={setSelectedPlayer}
            accessCtx={{ rooms, wings, roomRules, wingRules }}
          />
        )
      )}

      {showHallSchedule && me.currentRoom === 'HALLWAY' && (
        <SchedulePopup roomId="" currentUser={viewer} accessCtx={{ rooms, wings, roomRules, wingRules }} initialFullView={true} onClose={() => setShowHallSchedule(false)} onNavigateToRoom={(rid: string) => { setShowHallSchedule(false); handleScheduleNavigate(rid); }} />
      )}
      {returnRoomId && me.currentRoom !== 'HALLWAY' && me.currentRoom !== returnRoomId && rooms[returnRoomId] && (
        <div style={{position:'fixed', bottom:'18px', left:'50%', transform:'translateX(-50%)', zIndex:400}} className="flex items-center gap-2 bg-indigo-950/95 border border-indigo-400/40 rounded-2xl px-4 py-2 shadow-2xl">
          <button onClick={() => { const t = returnRoomId; setReturnRoomId(''); handleDoorClick(t); }} className="text-indigo-200 text-xs font-bold hover:text-white">{'\u2190'} Tilbage til {rooms[returnRoomId].name}</button>
          <button onClick={() => setReturnRoomId('')} className="text-indigo-400/60 hover:text-white text-xs">{'\u2715'}</button>
        </div>
      )}
      {!isPortalActive && (
        <>
          <div className="fixed bottom-6 left-6 z-[450] pointer-events-none">
            <div className="pointer-events-auto">
              <Chat 
                messages={messages} 
                onSendMessage={handleSendMessage} 
                onClearChat={handleClearChat}
                currentUser={me} 
                roomName={currentLocationName}
              />
            </div>
          </div>
          <div className="fixed top-3 right-3 md:top-6 md:right-6 z-[550] flex items-center gap-2 md:gap-4">
            
            {/* AKADEMIETS KOMPAS */}
            {academyUrl && (
              <a 
                href={academyUrl}
                onClick={(e) => { if (academyUrl.includes('halls.norewyn')) { e.preventDefault(); openInHalls(); } }} 
                target="_blank" 
                rel="noopener noreferrer"
                className="group relative flex items-center justify-center bg-indigo-900/60 hover:bg-yellow-500 border border-yellow-500/50 hover:border-black p-2 md:px-4 md:py-2 rounded-xl backdrop-blur-md transition-all duration-300 shadow-[0_0_20px_rgba(0,0,0,0.5)] hover:shadow-yellow-500/30"
              >
                <div className="flex items-center gap-2">
                  <span className="text-lg md:text-xl group-hover:rotate-[360deg] transition-transform duration-1000">🧭</span>
                  <span className="hidden md:block text-[9px] font-black uppercase tracking-widest text-indigo-100 group-hover:text-indigo-950 transition-colors">Tilbage til Akademiet</span>
                </div>
              </a>
            )}

            <div className="bg-indigo-950/80 border border-white/10 px-4 py-2 rounded-xl backdrop-blur-md hidden md:flex items-center gap-4">
               <span className="text-[10px] font-black uppercase text-indigo-400 tracking-widest">Rang:</span>
               <span className="text-yellow-500 font-bold text-xs">Level {me.magicalLevel}</span>
            </div>
            <button onClick={() => auth.signOut().then(() => setMe(null))} className="bg-white/10 hover:bg-white/20 border border-white/20 px-3 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl text-[8px] md:text-[10px] font-black uppercase tracking-widest transition-all">Log ud</button>
          </div>
          <DragonStatue role={me.role} onOpenMirror={handleOpenMirror} onOpenForge={() => handleOpenForge()} />
        </>
      )}

      {/* VOGTERENS SPEJL: Her er de fulde onUpdate/onDelete kald bevaret */}
      {showGuardianMirror && (
        <GuardianMirror 
          currentUser={me}
          onClose={() => setShowGuardianMirror(false)} 
          wings={wings} 
          rooms={rooms} 
          onUpdateWing={async (id, u) => setDoc(doc(db, "wings", id), u, { merge: true })} 
          onCreateWing={async (id, n) => setDoc(doc(db, "wings", id), { id, name: n, createdAt: Date.now() })} 
          onDeleteWing={async (id) => deleteDoc(doc(db, "wings", id))} 
          onUpdateRoom={async (id, u) => { await setDoc(doc(db, "rooms", id), u, { merge: true }); logSisterAction(me, 'campus', 'KONTROL', 'OPDATERET_RUM', id, (u && (u as any).name) || ''); }} 
          onCreateRoom={async (id, n) => { await setDoc(doc(db, "rooms", id), { id, name: n }); logSisterAction(me, 'campus', 'KREATION', 'OPRETTET_RUM', id, n); }} 
          onDeleteRoom={async (id) => { await deleteDoc(doc(db, "rooms", id)); logSisterAction(me, 'campus', 'KONTROL', 'SLETTET_RUM', id, ''); }} 
          setGuideMessage={setGuideMessage} 
        />
      )}
      
      {showTheForge && (
        <TheForge 
          onClose={() => { setShowTheForge(false); setForgeInitialRoomId(undefined); }} 
          rooms={rooms} 
          initialRoomId={forgeInitialRoomId} 
          canDelete={isAdmin(me.role)}
        />
      )}
      
      {selectedPlayer && (
        <PlayerProfile 
          player={selectedPlayer} 
          viewerRole={me ? me.role : undefined}
          onClose={() => setSelectedPlayer(null)} 
        />
      )}
    </div>
  );
};

export default App;